import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail, formatINR, formatDate, packageLabel, todayStr } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Search, Receipt, Loader2, Edit3, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth.jsx";
import EditBillDialog from "../components/EditBillDialog";

function CreateBillDialog({ customer, onCreated, periods }) {
  const [open, setOpen] = useState(false);
  const [period, setPeriod] = useState(String(periods[0]));
  const [amount, setAmount] = useState("");
  const [startDate, setStartDate] = useState(todayStr());
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true);
    try {
      await api.post("/bills", {
        customer_id: customer.id,
        period_months: Number(period),
        amount: Number(amount),
        start_date: startDate,
        note,
      });
      toast.success("Bill created");
      setOpen(false);
      setAmount("");
      setNote("");
      setStartDate(todayStr());
      onCreated?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" data-testid={`create-bill-${customer.id}`} className="bg-red-600 hover:bg-red-700">
          <Receipt className="h-3 w-3 mr-1" /> New Bill
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="create-bill-dialog">
        <DialogHeader>
          <DialogTitle>Create Bill — {customer.name}</DialogTitle>
          <DialogDescription>
            {customer.customer_code} · {packageLabel(customer.service_type, customer.package)}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Period</Label>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger data-testid="bill-period-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {periods.map((p) => (
                  <SelectItem key={p} value={String(p)}>{p} month{p > 1 ? "s" : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Amount (₹)</Label>
            <Input data-testid="bill-amount-input" type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
          <div>
            <Label>Start Date</Label>
            <Input data-testid="bill-start-date" type="date" max={todayStr()} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div>
            <Label>Note (optional)</Label>
            <Textarea data-testid="bill-note-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="bill-save-btn" disabled={loading || !amount} onClick={submit} className="bg-red-600 hover:bg-red-700">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create bill"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RecordPaymentDialog() { return null; }
// Record Payment moved to its own page (/record-payment). This stub kept just to
// preserve named imports if any. Safe to remove once verified.

function CustomerSearchRow({ serviceType, onCreatedBill, periods }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/customers", { params: { service_type: serviceType, q } });
        setResults(data);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q, serviceType]);

  return (
    <Card className="p-4">
      <h3 className="font-display font-semibold text-lg mb-3">Search & Create Bill</h3>
      <div className="relative max-w-md mb-3">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
        <Input data-testid="billing-customer-search" className="pl-9" placeholder="Search customer by name, phone, code…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Package</TableHead>
              <TableHead className="text-right">Balance</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {results.length === 0 && !loading && (
              <TableRow><TableCell colSpan={6} className="text-center text-gray-500 py-6">{q ? "No matches." : "Start typing to search customers."}</TableCell></TableRow>
            )}
            {results.slice(0, 8).map((c) => (
              <TableRow key={c.id} className="hover:bg-gray-50">
                <TableCell className="font-medium">{c.customer_code}</TableCell>
                <TableCell>{c.name}</TableCell>
                <TableCell>{c.phone}</TableCell>
                <TableCell>{packageLabel(c.service_type, c.package)}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {c.balance_due > 0 ? <span className="status-chip status-pending">{formatINR(c.balance_due)}</span> : <span className="text-gray-400 text-sm">—</span>}
                </TableCell>
                <TableCell className="text-right">
                  <CreateBillDialog customer={c} periods={periods} onCreated={onCreatedBill} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

function BillsList({ serviceType, refreshKey, onChange, periods }) {
  const { user } = useAuth();
  const [bills, setBills] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = { service_type: serviceType };
      if (filter !== "all") params.status = filter;
      const { data } = await api.get("/bills", { params });
      setBills(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [serviceType, filter, refreshKey]);

  const remove = async (id) => {
    if (!window.confirm("Delete this bill? Any payments recorded against it will also be removed and the customer balance will be reversed.")) return;
    try {
      await api.delete(`/bills/${id}`);
      toast.success("Bill deleted");
      load();
      onChange?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <Card className="p-4 mt-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-semibold text-lg">Bills</h3>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-36" data-testid="bills-filter-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="overflow-x-auto">
        <Table data-testid={`bills-table-${serviceType}`}>
          <TableHeader>
            <TableRow>
              <TableHead>Customer</TableHead>
              <TableHead>Package</TableHead>
              <TableHead>Period</TableHead>
              <TableHead>Active Until</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead className="text-right">Paid</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && bills.length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-center text-gray-500 py-6"><Loader2 className="h-4 w-4 animate-spin inline" /></TableCell></TableRow>
            )}
            {!loading && bills.length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-center text-gray-500 py-6">No bills.</TableCell></TableRow>
            )}
            {bills.map((b) => (
              <TableRow key={b.id} className="hover:bg-gray-50">
                <TableCell>
                  <div className="font-medium">{b.customer_name}</div>
                  <div className="text-xs text-gray-500">{b.customer_code}</div>
                </TableCell>
                <TableCell>{packageLabel(b.service_type, b.package)}</TableCell>
                <TableCell>{b.period_months} mo</TableCell>
                <TableCell className="text-xs">
                  {b.start_date && b.end_date ? (
                    <div>
                      <div className="text-gray-700">{b.end_date}</div>
                      <div className="text-gray-400">from {b.start_date}</div>
                    </div>
                  ) : <span className="text-gray-400">—</span>}
                </TableCell>
                <TableCell className="text-right tabular-nums">{formatINR(b.amount)}</TableCell>
                <TableCell className="text-right tabular-nums">{formatINR(b.paid_amount)}</TableCell>
                <TableCell>
                  <span className={`status-chip ${b.status === "paid" ? "status-paid" : "status-pending"}`}>
                    {b.status}
                  </span>
                </TableCell>
                <TableCell className="text-xs text-gray-500">{formatDate(b.created_at)}</TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex items-center gap-1">
                    {b.status !== "paid" && (
                      <Button
                        data-testid={`edit-bill-${b.id}`}
                        size="icon"
                        variant="ghost"
                        onClick={() => setEditing(b)}
                        title="Edit bill"
                      >
                        <Edit3 className="h-4 w-4 text-gray-600" />
                      </Button>
                    )}
                    {user?.role === "admin" && (
                      <Button
                        data-testid={`delete-bill-${b.id}`}
                        size="icon"
                        variant="ghost"
                        onClick={() => remove(b.id)}
                        title="Delete bill"
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <EditBillDialog
        bill={editing}
        periods={periods}
        open={!!editing}
        onOpenChange={(v) => !v && setEditing(null)}
        onDone={() => { setEditing(null); load(); onChange?.(); }}
      />
    </Card>
  );
}

export default function BillingPage() {
  const [catalog, setCatalog] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [tab, setTab] = useState("internet");

  useEffect(() => {
    api.get("/catalog").then((r) => setCatalog(r.data));
  }, []);

  const bump = () => setRefreshKey((k) => k + 1);

  const periods = useMemo(() => {
    if (!catalog) return [1];
    return tab === "internet" ? catalog.internet_periods : catalog.cable_periods;
  }, [catalog, tab]);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-3xl font-display font-bold tracking-tight">Billing</h1>
        <p className="text-gray-500 mt-1 text-sm">Create bills here. Record payments from the <span className="font-medium text-red-700">Record Payment</span> tab in the sidebar.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-gray-200">
          <TabsTrigger value="internet" data-testid="billing-tab-internet">Internet</TabsTrigger>
          <TabsTrigger value="cable" data-testid="billing-tab-cable">Cable TV</TabsTrigger>
        </TabsList>

        <TabsContent value="internet" className="mt-4 space-y-4">
          <CustomerSearchRow serviceType="internet" onCreatedBill={bump} periods={periods} />
          <BillsList serviceType="internet" refreshKey={refreshKey} onChange={bump} periods={periods} />
        </TabsContent>
        <TabsContent value="cable" className="mt-4 space-y-4">
          <CustomerSearchRow serviceType="cable" onCreatedBill={bump} periods={periods} />
          <BillsList serviceType="cable" refreshKey={refreshKey} onChange={bump} periods={periods} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
